import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import Badge from "./Badge";
import socket from "../../socket";

const LiveUserRankBadge = ({
  clientId,
  isSmDown,
  compact = false,
  textColor = "#fff",
  showLevel = true,
  showBadge = true,
}) => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuth();
  const [canonicalId, setCanonicalId] = useState(null);
  const [canonicalResolved, setCanonicalResolved] = useState(false);
  const [xp, setXp] = useState(0);
  const [lvl, setLvl] = useState(1);
  const [thresholds, setThresholds] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeRankId = useMemo(() => {
    if (!clientId) return "";
    if (!isSignedIn) return String(clientId);
    if (!canonicalResolved) return "";
    return String(canonicalId || clientId);
  }, [clientId, isSignedIn, canonicalResolved, canonicalId]);

  // 1) Canonical
  useEffect(() => {
    let cancelled = false;

    async function fetchCanonical() {
      setCanonicalResolved(false);

      if (!clientId) {
        setCanonicalId(null);
        setXp(0);
        setLvl(1);
        setLoading(false);
        setCanonicalResolved(true);
        return;
      }

      if (!isSignedIn) {
        setCanonicalId(String(clientId));
        setCanonicalResolved(true);
        return;
      }

      try {
        const res = await fetch(
          `/api/rank/canonical?clientId=${encodeURIComponent(clientId)}`,
          {
            credentials: "include",
            headers: { "x-client-id": clientId }, // ✅ important (cohérent partout)
          }
        );
        const json = await res.json();
        if (cancelled) return;

        setCanonicalId(String(json.canonicalRankId || clientId));
        setCanonicalResolved(true);
      } catch (e) {
        console.error("fetchCanonical error:", e);
        if (!cancelled) {
          setCanonicalId(String(clientId));
          setCanonicalResolved(true);
        }
      }
    }

    fetchCanonical();
    return () => {
      cancelled = true;
    };
  }, [clientId, isSignedIn]);

  // 2) Initial fetch config + state
  useEffect(() => {
    let cancelled = false;

    async function fetchInitial() {
      if (!activeRankId) return;

      try {
        setLoading(true);

        const [configRes, rankRes] = await Promise.all([
          fetch("/api/rank/config", { credentials: "include" }),
          fetch(`/api/rank/state?userId=${encodeURIComponent(activeRankId)}`, {
            credentials: "include",
          }),
        ]);

        const configJson = await configRes.json();
        const rankJson = await rankRes.json();

        if (cancelled) return;

        setThresholds({
          bronze_xp: Number(configJson.bronze_xp || 0),
          silver_xp: Number(configJson.silver_xp || 0),
          gold_xp: Number(configJson.gold_xp || 0),
          uranium_xp: Number(configJson.uranium_xp || 0),
        });

        setXp(Number(rankJson.xp || 0));
        setLvl(Number(rankJson.lvl || 1));
      } catch (e) {
        console.error("LiveUserRankBadge initial fetch error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, [activeRankId]);

  // 3) Socket subscribe: une seule source de vérité active
  useEffect(() => {
    if (!socket || !activeRankId) return;

    const handleRankUpdate = (payload) => {
      if (!payload) return;
      const pid = String(payload.userId || "");
      if (pid !== activeRankId) return;

      setXp(Number(payload.xp || 0));
      setLvl(Number(payload.lvl || 1));
    };

    socket.on("rank:update", handleRankUpdate);
    socket.emit("rank:subscribe", { userId: activeRankId });

    return () => {
      socket.off("rank:update", handleRankUpdate);
      socket.emit("rank:unsubscribe", { userId: activeRankId });
    };
  }, [activeRankId]);


  if (!showLevel && !showBadge) {
    return null;
  }

  if (!clientId || loading || !thresholds || (isSignedIn && !canonicalResolved)) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          mr: compact ? 0.5 : { xs: 1, sm: 1.5 },
        }}
      >
        <CircularProgress size={isSmDown ? 16 : 20} />
      </Box>
    );
  }

  return (
    <Box
      component={Link}
      to="/ranking"
      state={{ activeTab: "progression" }}
      aria-label={t("navigation.rankings")}
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        mr: compact ? 0.5 : { xs: 1, sm: 1.5 },
        textDecoration: "none",
        cursor: "pointer",
        minWidth: 0,
      }}
    >
      {showLevel ? (
        <Box
          className="teko-label"
          component="span"
          sx={{
            mr: showBadge ? { xs: 0.5, sm: 1 } : 0,
            fontSize: compact
              ? { xs: "1rem !important", sm: "1.12rem !important", md: "1.2rem !important" }
              : { xs: "1rem !important", sm: "1.12rem !important", md: "1.2rem !important" },
            color: textColor,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          Lvl. {lvl}
        </Box>
      ) : null}

      {showBadge ? (
        <Badge
          xp={xp}
          thresholds={thresholds}
          size={compact ? 20 : isSmDown ? 20 : 28}
        />
      ) : null}
    </Box>
  );
};

export default LiveUserRankBadge;
